import { useEffect, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { SignIn, useUser } from '@clerk/clerk-react'

// Stable appearance config - defined outside component to prevent re-creation
const signInAppearance = {
  layout: {
    socialButtonsPlacement: 'bottom' as const,
    socialButtonsVariant: 'blockButton' as const,
    showOptionalFields: false,
  },
  elements: {
    // Root & Card - completely transparent
    rootBox: 'cl-root-box',
    card: 'cl-card',
    cardBox: 'cl-card-box',

    // Hide all Clerk headers and branding
    header: 'cl-hidden',
    headerTitle: 'cl-hidden',
    headerSubtitle: 'cl-hidden',
    logoBox: 'cl-hidden',
    logoImage: 'cl-hidden',

    // Form styling
    form: 'cl-form',
    formFieldRow: 'cl-form-field-row',
    formFieldLabel: 'cl-form-field-label',
    formFieldInput: 'cl-form-field-input',
    formFieldInputShowPasswordButton: 'cl-show-password-btn',
    formButtonPrimary: 'cl-button-primary',
    formFieldAction: 'cl-form-field-action',

    // Social buttons
    socialButtons: 'cl-social-buttons',
    socialButtonsBlockButton: 'cl-social-button',
    socialButtonsBlockButtonText: 'cl-social-button-text',
    socialButtonsProviderIcon: 'cl-social-icon',

    // Divider
    dividerRow: 'cl-divider-row',
    dividerLine: 'cl-divider-line',
    dividerText: 'cl-divider-text',

    // Footer - hide Clerk branding completely
    footer: 'cl-hidden',
    footerAction: 'cl-hidden',
    footerActionLink: 'cl-hidden',
    footerPages: 'cl-hidden',
    footerPagesLink: 'cl-hidden',

    // Hide any powered by or branding
    badge: 'cl-hidden',

    // Alerts
    alert: 'cl-alert',
    alertText: 'cl-alert-text',

    // Identity preview (for returning users)
    identityPreview: 'cl-identity-preview',
    identityPreviewText: 'cl-identity-preview-text',
    identityPreviewEditButton: 'cl-identity-preview-edit',

    // Alternative methods
    alternativeMethods: 'cl-alternative-methods',
    alternativeMethodsBlockButton: 'cl-alternative-method-btn',
  },
  variables: {
    colorPrimary: '#8b3621',
    colorText: '#1e1c1c',
    colorTextSecondary: '#444',
    colorBackground: '#fffdf8',
    colorInputBackground: '#ffffff',
    colorInputText: '#1e1c1c',
    fontFamily: '"DM Sans", sans-serif',
    borderRadius: '0.5rem',
  },
}

/**
 * Página de inicio de sesión con diseño A1Homes.
 * Redirige al dashboard si el usuario ya está autenticado.
 */
export function Login() {
  const navigate = useNavigate()
  const location = useLocation()
  const { isSignedIn, isLoaded } = useUser()

  // Memoize the redirect destination to prevent unnecessary re-renders
  const from = useMemo(() => {
    return (location.state as { from?: Location })?.from?.pathname || '/dashboard'
  }, [location.state])

  // Redirect if already authenticated
  useEffect(() => {
    if (isLoaded && isSignedIn) {
      navigate(from, { replace: true })
    }
  }, [isSignedIn, isLoaded, navigate, from])

  // Show loading state while Clerk loads
  if (!isLoaded) {
    return (
      <div className="login-page">
        <div className="login-page__background">
          <img src="/login-bg.webp" alt="" aria-hidden="true" />
          <div className="login-page__overlay" />
        </div>
        <div className="login-page__content">
          <div className="login-card">
            <div className="login-card__loader">
              <div className="login-card__spinner" />
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="login-page">
      {/* Background with overlay - matching landing page hero */}
      <div className="login-page__background">
        <img src="/login-bg.webp" alt="" aria-hidden="true" />
        <div className="login-page__overlay" />
      </div>

      {/* Content */}
      <div className="login-page__content">
        {/* Branding */}
        <div className="login-page__branding">
          <a href="https://a1homes.es" className="login-page__logo">
            A1Homes
          </a>
        </div>

        {/* Login Card */}
        <div className="login-card">
          <div className="login-card__header">
            <h1 className="login-card__title">Panel de Administración</h1>
            <p className="login-card__subtitle">
              Accede para gestionar propiedades y contenido
            </p>
          </div>

          <div className="login-card__form">
            <SignIn
              appearance={signInAppearance}
              routing="hash"
              forceRedirectUrl={from}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="login-page__footer">
          <p>Gestión de propiedades inmobiliarias en Alicante</p>
        </div>
      </div>
    </div>
  )
}
